import i18next, { TFunction } from 'i18next';
import moment from 'moment';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useIsSupport, useWorkspace } from '@treams/treams-hooks';
import { SearchIcon } from '@treams/treams-icons';
import {
	FeatureName,
	FeatureStability,
	WorkspaceFeatureFragment,
	WorkspaceFeatureStatus,
	useApproveFeatureForWorkspaceMutation,
	useUpdateWorkspaceFeatureMutation,
	useWorkspaceFeaturesQuery,
} from '@treams/treams-network';
import {
	Button,
	Callout,
	Checkbox,
	Empty,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Modal,
	Paragraph,
	Pill,
	Select,
	Spinner,
	tokens,
} from '@treams/treams-ui';

type WorkspaceFeatureItem = WorkspaceFeatureFragment & { blacklist?: boolean };

const SearchContainer = styled(Box)`
	background: ${tokens.surface.primary};
	flex-shrink: 0;
`;

function featureIdKey(featureId: string) {
	return featureId.replace(/\./g, '_');
}

function getFeatureLabel(featureId: string, t: TFunction) {
	const key = `settings.organization.settings.${featureIdKey(featureId)}`;
	return i18next.exists(key) ? t(key) : featureIdKey(featureId);
}

function getFeatureDescription(featureId: string, t: TFunction) {
	const key = `settings.organization.settings.descriptions.${featureIdKey(featureId)}`;
	return i18next.exists(key) ? t(key) : undefined;
}

function normalizeSearch(value: string) {
	return value
		.toLowerCase()
		.replace(/[._-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function textMatches(haystack: string, needle: string) {
	if (!needle) {
		return true;
	}

	return normalizeSearch(haystack).includes(needle);
}

function filterFeatureSections(
	sections: Record<string, WorkspaceFeatureItem[]>,
	query: string,
	t: TFunction,
): [string, WorkspaceFeatureItem[]][] {
	const needle = normalizeSearch(query);

	return Object.entries(sections).flatMap(([name, features]) => {
		if (features.length === 0) {
			return [];
		}

		if (!needle) {
			return [[name, features]];
		}

		const sectionTitle = t(`settings.features.${name}.title`);
		const sectionMatches = textMatches(sectionTitle, needle) || textMatches(name, needle);
		const matchedFeatures = sectionMatches
			? features
			: features.filter((feature) => {
					const id = feature.feature.id;
					const label = getFeatureLabel(id, t);
					const description = getFeatureDescription(id, t) ?? '';

					return [id, label, description].some((text) => textMatches(text, needle));
				});

		return matchedFeatures.length > 0 ? [[name, matchedFeatures]] : [];
	});
}

function WorkspaceFeature(props: { feature: WorkspaceFeatureItem }) {
	const { t } = useTranslation();
	const isSupport = useIsSupport();

	const [update, { loading }] = useUpdateWorkspaceFeatureMutation();
	const [approve, { loading: approving, error: approveError }] =
		useApproveFeatureForWorkspaceMutation();

	const [error, attempt] = useErrorBoundary();

	const updateWorkspaceFeature = attempt(
		async (featureID: FeatureName, status: WorkspaceFeatureStatus) => {
			await update({ variables: { input: { featureID, status } } });
		},
	);

	const statuses = [
		WorkspaceFeatureStatus.Enabled,
		WorkspaceFeatureStatus.Superuser,
		WorkspaceFeatureStatus.Disabled,
	];

	if (isSupport) {
		statuses.push(WorkspaceFeatureStatus.Restricted);
	}

	const options = statuses.map((status) => ({
		value: status,
		title: t(`settings.organization.featureStatus.${status.toLowerCase()}`),
	}));

	const approveFeatureConditions = attempt(async () => {
		if (!props.feature.requiredConditions) {
			throw new Error('No required conditions for feature found');
		}

		await approve({
			variables: {
				input: {
					featureID: props.feature.feature.id,
					conditionsVersion: props.feature.requiredConditions.currentVersion,
				},
			},
			update(cache, { data }) {
				const approval = data?.approveFeatureForWorkspace.approval;
				if (!approval) {
					return;
				}

				const featureCacheId = cache.identify({
					__typename: 'WorkspaceFeature',
					id: props.feature.id,
				});
				if (!featureCacheId) {
					return;
				}

				cache.modify({
					id: featureCacheId,
					fields: {
						needsWorkspaceApproval: () => false,
						workspaceApproval: () => ({
							__typename: 'FeatureApproval',
							approvedAt: approval.approvedAt,
							approvedBy: {
								__typename: 'User',
								id: approval.approvedBy.id,
								username: approval.approvedBy.username,
							},
						}),
					},
				});
			},
		});
	});

	const featureLabel = getFeatureLabel(props.feature.feature.id, t);
	const featureDescription = getFeatureDescription(props.feature.feature.id, t);

	if (props.feature.needsWorkspaceApproval) {
		if (!props.feature.requiredConditions) {
			return null;
		}

		return (
			<Flex>
				<Callout color="blue">
					<Box>
						<GraphQLErrorMessage error={error || approveError} />
						<Heading3>{featureLabel}</Heading3>
						<Paragraph>
							{t('settings.organization.settings.needsApproval.description')}{' '}
							<a
								href={props.feature.requiredConditions.linkToConditions}
								target="_blank"
								rel="noreferrer"
								style={{ textDecoration: 'underline' }}
							>
								{t('settings.organization.settings.needsApproval.link')}
							</a>
						</Paragraph>
						<Box marginTop={3} maxWidth={200}>
							<Button
								type="transparent"
								title={t('settings.organization.settings.needsApproval.accept')}
								small
								loading={approving}
								onClick={approveFeatureConditions}
							/>
						</Box>
					</Box>
				</Callout>
			</Flex>
		);
	}

	const featureHasBeenAcceptedRecently =
		props.feature.workspaceApproval?.approvedAt &&
		moment(props.feature.workspaceApproval.approvedAt).isAfter(moment().subtract(30, 'days'));

	const content = (
		<Flex flexDirection="row" alignItems="center">
			{!props.feature.inPlan && <Checkbox disabled checked={false} />}
			<Box
				flexGrow={1}
				flexShrink={1}
				style={{
					backgroundColor: isSupport && props.feature.blacklist ? '#F5F9B7' : 'none',
				}}
			>
				<Box>
					{featureLabel}
					{props.feature.feature.stability === FeatureStability.Alpha && (
						<Pill ml={2} tint="green" small>
							alpha
						</Pill>
					)}

					{props.feature.feature.stability === FeatureStability.Beta && (
						<Pill ml={2} tint="primary" small>
							beta
						</Pill>
					)}
				</Box>
				<Paragraph small>{featureDescription}</Paragraph>
			</Box>
			{props.feature.inPlan && (
				<Box width={200} ml={2} flexGrow={0} flexShrink={0}>
					{loading ? (
						<Select.Skeleton />
					) : (
						<Select
							search={false}
							selectedValue={props.feature.status}
							options={options}
							onChange={(option) => {
								updateWorkspaceFeature(props.feature.feature.id, option! as WorkspaceFeatureStatus);
							}}
						/>
					)}
				</Box>
			)}
		</Flex>
	);

	if (props.feature.requiredConditions && featureHasBeenAcceptedRecently) {
		return (
			<Callout color="blue">
				<Box flexGrow={1}>
					<GraphQLErrorMessage error={error} />
					<Heading3 mb={2}>
						{t('settings.organization.settings.needsApproval.recentlyAccepted')}
					</Heading3>
					{content}
				</Box>
			</Callout>
		);
	}

	return (
		<Box py={2}>
			<GraphQLErrorMessage error={error} />
			{content}
		</Box>
	);
}

function useWorkspaceFeatures(
	map: Record<string, (FeatureName | { feature: FeatureName; blacklist: boolean })[]>,
): {
	loading: boolean;
	features?: Record<string, WorkspaceFeatureItem[]>;
	notFoundFeatures?: string[];
} {
	const workspace = useWorkspace();
	const isSupport = useIsSupport();

	const { data } = useWorkspaceFeaturesQuery({
		variables: {
			id: workspace.id,
		},
	});

	if (!data) {
		return { loading: true };
	}

	const workspaceFeatures =
		data?.workspace.features.filter(
			(feature) => feature.feature.stability !== FeatureStability.Alpha || isSupport,
		) ?? [];

	const featuresUsed: Record<string, boolean> = {};
	const features: Record<string, WorkspaceFeatureItem[]> = {
		unused: [],
	};
	const notFoundFeatures: string[] = [];

	for (const section in map) {
		// For each section, exchange the feature names for the feature objects
		const featureArray: WorkspaceFeatureItem[] = [];
		for (const featureName of map[section]) {
			const string = typeof featureName === 'string' ? featureName : featureName.feature;
			const feature = workspaceFeatures.find((f) => f.feature.id === string);

			if (!feature) {
				notFoundFeatures.push(string);
				continue;
			}

			featureArray.push({
				...feature,
				blacklist: typeof featureName === 'object' ? featureName.blacklist : undefined,
			});
			featuresUsed[string] = true;
		}
		features[section] = featureArray;
	}

	const unusedFeatures = workspaceFeatures.filter((feature) => !featuresUsed[feature.feature.id]);
	if (unusedFeatures.length > 0) {
		features['unused'] = unusedFeatures;
	}

	return { features, notFoundFeatures, loading: false };
}

export default function WorkspaceFeatures(props: { visible?: boolean; onClose?: () => void }) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	useLayoutEffect(() => {
		if (props.visible === false) {
			return;
		}

		const frame = requestAnimationFrame(() => {
			inputRef.current?.focus();
		});

		return () => cancelAnimationFrame(frame);
	}, [props.visible]);

	const map: Record<string, (FeatureName | { feature: FeatureName; blacklist: boolean })[]> = {
		ai: [
			FeatureName.Ai,
			FeatureName.AiAgent,
			FeatureName.AdvancedAgent,
			FeatureName.ConversationAiInsights,
			FeatureName.ConversationTranscriptions,
			FeatureName.SoundRecorderV2,
			FeatureName.ExternalMcp,
		],
		conversations: [
			FeatureName.Evaluations,
			FeatureName.EvaluationsAdhoc,
			FeatureName.EvaluationsOverride,
			FeatureName.EvaluationsContext,
			FeatureName.ConversationOnlyWithManager,
			FeatureName.HideYouAsConversationPartner,
			FeatureName.ConversationPlanning,
			FeatureName.HideConversationDeadlineConfig,
			FeatureName.ConversationsOptionalQuestions,
			{ feature: FeatureName.OnlyAllowSigningWhenEmployeeHasSigned, blacklist: true },
			{ feature: FeatureName.FinalScoreSumCalculation, blacklist: true },
		],
		goals: [
			FeatureName.Goals,
			FeatureName.TeamGoals,
			FeatureName.AddGoalsToOrganisationGoals,
			FeatureName.CompetencyGoals,
			{ feature: FeatureName.DevelopmentPlan, blacklist: true },
			{ feature: FeatureName.GoalsAudit, blacklist: true },
			{ feature: FeatureName.JobGoalTemplates, blacklist: true },
			{ feature: FeatureName.GoalLabels, blacklist: true },
			{ feature: FeatureName.GoalsParentDepartmentGoals, blacklist: true },
		],
		feedback: [
			FeatureName.Feedback,
			FeatureName.Compliments,
			FeatureName.FeedbackAdhoc,
			FeatureName.FeedbackGoals,
			FeatureName.FeedbackCompetencies,
			FeatureName.FeedbackSpontaneous,
			FeatureName.FeedbackVoiceMemos,
			FeatureName.CompetencyFeedbackNotObservedOption,
			{ feature: FeatureName.FeedbackReply, blacklist: true },
			{ feature: FeatureName.FeedbackShare, blacklist: true },
			{ feature: FeatureName.FeedbackLockCycleTemplate, blacklist: true },
		],
		notes: [FeatureName.Notes, FeatureName.OneOnOnes],
		engagement: [
			FeatureName.Engagement,
			{ feature: FeatureName.EngagementReminder, blacklist: true },
			{ feature: FeatureName.EngagementEndPage, blacklist: true },
			{ feature: FeatureName.OrderedEngagementSurveys, blacklist: true },
			{ feature: FeatureName.EngagementReply, blacklist: true },
		],
		skills: [
			FeatureName.GrowPath,
			FeatureName.JobCompetenciesExplorer,
			FeatureName.SearchOnSkills,
			FeatureName.CompetencyDetailScreen,
			FeatureName.MultiRoleConversion,
			{ feature: FeatureName.ModuleSkills, blacklist: true },
			{ feature: FeatureName.JobCompetencies, blacklist: true },
			{ feature: FeatureName.AppraisalsIncludeCompetenciesFromConversations, blacklist: true },
		],
		talentReviews: [
			FeatureName.TalentReviews,
			FeatureName.HideOwnerInTalentReview,
			FeatureName.TalentReviewsScores,
			{ feature: FeatureName.TalentReviewsV2, blacklist: true },
			FeatureName.Succession,
		],
		learning: [FeatureName.Learning, FeatureName.Goodhabitz],
		cycles: [
			FeatureName.Cycles,
			{ feature: FeatureName.CycleCompletedOnDurationExceeded, blacklist: true },
		],
		analytics: [
			FeatureName.Analytics,
			FeatureName.CycleAnalytics,
			FeatureName.TreamsScore,
			FeatureName.TalentReviewAnalytics,
			{ feature: FeatureName.AnalyticsFeedbackOnCompetencies, blacklist: true },
			{ feature: FeatureName.AnalyticsTeams, blacklist: true },
			{ feature: FeatureName.AnalyticsIncludeCompetenciesFromConversations, blacklist: true },
		],
		rewards: [FeatureName.Rewards],
		advanced: [
			{ feature: FeatureName.ShowRecommendPage, blacklist: false },
			FeatureName.ScimIncludeCustomSchemaPrefix,
			FeatureName.CustomEmailDomain,
			FeatureName.CustomEmailDomainOptionalMailfrom,
			FeatureName.QuillPhotoVideo,
			FeatureName.Helicopter,
			{ feature: FeatureName.Checkpoints, blacklist: true },
			{ feature: FeatureName.Mfa, blacklist: true },
			{ feature: FeatureName.Billing, blacklist: true },
			{ feature: FeatureName.ScimSsoFix, blacklist: true },
			{ feature: FeatureName.HrBusinessPartnerRole, blacklist: true },
			{ feature: FeatureName.ProductBeamer, blacklist: true },
			FeatureName.ApiPermissions,
		],
	};

	const { loading, features: sections, notFoundFeatures } = useWorkspaceFeatures(map);

	const filteredSections = useMemo(
		() => (sections ? filterFeatureSections(sections, query, t) : []),
		[sections, query, t],
	);

	console.log('Not found features:', notFoundFeatures);

	return (
		<>
			<SearchContainer px={3} pt={3}>
				<Input
					search
					autoFocus
					inputRef={inputRef}
					icon={<SearchIcon />}
					placeholder={t('settings.features.search.placeholder', 'Search modules...')}
					value={query}
					onChange={setQuery}
					onKeyDown={(event) => {
						if (event.key !== 'Escape') {
							return;
						}

						if (query) {
							event.preventDefault();
							setQuery('');
							return;
						}

						props.onClose?.();
					}}
				/>
			</SearchContainer>
			<Modal.Contents>
				{loading ? (
					<Spinner centered />
				) : filteredSections.length > 0 ? (
					filteredSections.map(([name, features]) => (
						<Box mb={4} key={name}>
							<Heading3 mb={2}>{t(`settings.features.${name}.title`)}</Heading3>
							{features.map((feature) => (
								<WorkspaceFeature key={feature.feature.id} feature={feature} />
							))}
						</Box>
					))
				) : (
					<Flex justifyContent="center" py={4}>
						<Empty
							small
							icon={<SearchIcon />}
							title={t('settings.features.search.noResults', 'No modules found')}
							description={t(
								'settings.features.search.noResultsDescription',
								'Try a different name or feature ID',
							)}
						/>
					</Flex>
				)}
			</Modal.Contents>
		</>
	);
}
